/**
 * IndexedDB storage for appraisal photos — durable raw-byte store (schema v2).
 *
 * WHY RAW BYTES (v2):
 * -------------------
 * v1 stored a `Blob` in IndexedDB. On iOS/WKWebView that is not restart-safe:
 * the stored value can remain a *reference* whose backing data is only valid
 * for the session that created it. After the app/WebView was recreated,
 * `blob.arrayBuffer()` stalled for ~60s and then threw `NotFoundError`, and the
 * uploader wrote the photo off as a purged camera temp file. Production 1.56
 * lost photos this way.
 *
 * v2 stores the actual image bytes as an `ArrayBuffer` (structured clone), so
 * the persisted record contains the data itself — nothing to resolve, nothing
 * to re-open, no session affinity. Legacy v1 Blob records are still readable
 * and are opportunistically upgraded to raw bytes when they can be read.
 *
 * Capture is only reported successful after the bytes are written, the
 * transaction has completed, the record is read back in a FRESH transaction,
 * and the returned bytes are non-empty and convertible to an image payload.
 */

import { isRecoverableDbError, reportRecovery } from "@/lib/offlineDb";
import { noteStorageOperationSucceeded } from "@/lib/offlineDb";
import { logOfflineEvent } from "@/lib/offlineTelemetry";
import { PhotoPipelineError, isQuotaExceededError } from "@/lib/photoFailureKinds";
import { sha256Hex } from "@/lib/photoIntegrity";

const DB_NAME = "transportmate_appraisal_photos";
/** v2: raw-byte (`bytes: ArrayBuffer`) representation. */
const DB_VERSION = 2;
const STORE_NAME = "photos";
export const PHOTO_SCHEMA_VERSION = 2;

/** Default bound on materialising durable bytes (a broken record must not block the queue). */
export const DURABLE_READ_TIMEOUT_MS = 8_000;

/**
 * "needs_review" is an internal exception state, never a driver instruction.
 * It is only ever set after the defined terminal recovery policy has been
 * exhausted (repeated durable-read failures AND no object anywhere in Storage
 * under the appraisal prefix). The record and its database reference are always
 * kept; the item is raised with support for manual handling rather than asking
 * a driver to re-photograph a vehicle that has probably already been delivered.
 */
export type PhotoStatus =
  | "pending_local"
  | "uploading"
  | "uploaded"
  | "failed"
  | "needs_review"
  /**
   * One-time archival of exception records demonstrably produced by the broken
   * pre-1.58 Blob pipeline (no raw bytes, unreadable blob, no Storage object).
   * The record and its diagnostics are retained; it is simply no longer a
   * *current* action item. Never applied to v2 (raw-byte) records.
   */
  | "archived_legacy"
  /**
   * Terminal tombstone (1.63.x): the driver explicitly deleted/replaced this
   * photo. Set BEFORE the record is removed so an upload that is still in
   * flight can observe the deletion and refuse to resurrect the photo into
   * `photo_urls`. Never set by the queue, and never retried.
   */
  | "deleted";

export interface AppraisalPhotoRecord {
  /** Composite key: `${appraisalId}:${localId}` */
  id: string;
  appraisalId: string;
  localId: string;
  /** "walkaround:front" or "part:front-bumper:0" */
  category: string;
  /** v2 durable representation — the actual image bytes. */
  bytes?: ArrayBuffer;
  byteLength?: number;
  mimeType?: string;
  /** Record schema version; absent/1 means a legacy Blob record. */
  schemaVersion?: number;
  /** v1 legacy representation. Never written by new captures. */
  blob?: Blob;
  status: PhotoStatus;
  storagePath: string | null;
  createdAt: number;
  updatedAt: number;
  /** Deterministic destination path in the `appraisal-photos` bucket (durable queue) */
  targetPath?: string;
  /** Durable upload retry bookkeeping */
  attempts?: number;
  nextRetryAt?: number;
  lastError?: string | null;
  /** Failure category of the most recent attempt (see PhotoFailureKind). */
  lastErrorKind?: string | null;
  /** Consecutive failures to materialise the durable bytes. */
  durableReadFailures?: number;
  /** Server reconciliation refused by permissions (e.g. job reassigned). Held for review; not retried automatically. Never set uploaded. */
  reconcileHeldAt?: number;
  /** Set only after the uploaded path is reconciled into the database record used by previews/PDFs. */
  dbSyncedAt?: number;
  /** Support diagnostics — lifecycle timestamps (never image content). */
  persistedAt?: number;
  queuedAt?: number;
  firstAttemptAt?: number;
  lastAttemptAt?: number;
  storageVerifiedAt?: number;
  /**
   * 1.81.x — the exact Storage path whose object was verified for this record
   * (stage 1 complete). While it equals `targetPath`, a failed database
   * attachment retries the attachment only and never re-uploads the bytes:
   * a re-upload is an overwrite (UPDATE), which Storage RLS denies to drivers.
   */
  storageVerifiedPath?: string;
  /** Set only by the one-time legacy-exception archival (diagnostics preserved). */
  archivedAt?: number;
  archivedReason?: string;
  /** Set when the driver deleted/replaced the photo (tombstone, see PhotoStatus). */
  deletedAt?: number;
  /**
   * H3/H4 (1.60.51) — byte integrity metadata for the durable payload.
   * Optional and backward compatible: records captured before 1.60.51, and all
   * legacy v1 records, simply do not have them and are never treated as failed
   * or unverifiable because of it.
   */
  expectedByteLength?: number;
  sha256?: string | null;
  integrityBackfilledAt?: number;

  /**
   * Observability bookkeeping only (1.60.50). Neither field participates in any
   * queue, retry, verification or archival decision — they exist purely so a
   * lifecycle telemetry event is emitted once per photo rather than on every
   * boot recovery pass.
   */
  terminalTelemetryAt?: number;
  dbPendingTelemetryAt?: number;
}

let dbInstance: IDBDatabase | null = null;
let opening: Promise<IDBDatabase> | null = null;

/** Drop the cached handle so the next call reopens a fresh connection. */
function resetConnection(reason: string) {
  if (dbInstance) {
    console.warn(`[AppraisalPhotoDB] Connection dropped (${reason}) — reopening on next access`);
  }
  dbInstance = null;
  opening = null;
}

function openDb(): Promise<IDBDatabase> {
  if (dbInstance) return Promise.resolve(dbInstance);
  if (opening) return opening;
  opening = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      // v1 → v2 is additive: the store and its indexes are unchanged, records
      // gain a `bytes` field. Existing Blob records stay valid and readable;
      // they are distinguished at read time by the absence of `schemaVersion`.
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("by_appraisal", "appraisalId", { unique: false });
        store.createIndex("by_status", "status", { unique: false });
      }
    };
    req.onsuccess = () => {
      dbInstance = req.result;
      // iOS/WKWebView closes the connection while backgrounded; without this
      // the stale handle is reused and every photo write fails silently.
      dbInstance.onclose = () => resetConnection("onclose");
      dbInstance.onversionchange = () => {
        try { dbInstance?.close(); } catch { /* ignore */ }
        resetConnection("versionchange");
      };
      dbInstance.onerror = () => resetConnection("db error");
      resolve(dbInstance);
    };
    req.onerror = () => { opening = null; reject(req.error); };
  });
  return opening;
}

/**
 * Retry once when WKWebView closes the cached connection during backgrounding
 * or loses the backend transaction ("Attempt to get records from database
 * without an in-progress transaction"). Uses the SAME classifier and recovery
 * telemetry as offlineDb (8155d73e): close + reset the stale handle, reopen,
 * retry the same operation once, rethrow unchanged on failure. Never deletes
 * or rewrites records.
 */
async function withDb<T>(operation: (db: IDBDatabase) => Promise<T>, op = "appraisal_photos"): Promise<T> {
  try {
    const firstTry = await operation(await openDb());
    noteStorageOperationSucceeded();
    return firstTry;
  } catch (error: any) {
    if (!isRecoverableDbError(error)) throw error;
    const message = error?.message || String(error);
    const stale = dbInstance;
    resetConnection(`retry after: ${message}`);
    try { stale?.close(); } catch { /* ignore */ }
    try {
      const result = await operation(await openDb());
      reportRecovery("indexeddb_recovery_succeeded", op, error);
      return result;
    } catch (retryErr) {
      resetConnection(`retry failed: ${(retryErr as any)?.message || String(retryErr)}`);
      reportRecovery("indexeddb_recovery_failed", op, error, retryErr);
      throw retryErr;
    }
  }
}

function toArrayBuffer(view: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(view.byteLength);
  copy.set(view);
  return copy.buffer;
}

async function blobToBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof (blob as any).arrayBuffer === "function") {
    return new Uint8Array(await blob.arrayBuffer());
  }
  if (typeof Response !== "undefined") {
    return new Uint8Array(await new Response(blob).arrayBuffer());
  }
  return new Promise<Uint8Array>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error || new Error("Photo data is unreadable"));
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Read the bytes out of whatever the camera handed us, at capture time.
 * A failure here — and only here — may be attributed to the camera source.
 */
async function readCameraBytes(blob: Blob): Promise<Uint8Array> {
  if (!blob) {
    throw new PhotoPipelineError("camera_source_unavailable", "Captured image is missing");
  }
  if (typeof blob.size === "number" && blob.size === 0) {
    throw new PhotoPipelineError("camera_source_unavailable", "Captured image is empty (0 bytes)");
  }
  let bytes: Uint8Array;
  try {
    bytes = await blobToBytes(blob);
  } catch (error: any) {
    if (error?.name === "NotFoundError") {
      throw new PhotoPipelineError(
        "camera_source_unavailable",
        "Captured image file no longer exists (camera source unavailable)",
        { cause: error },
      );
    }
    throw new PhotoPipelineError("camera_source_unavailable", error?.message || "Captured image is unreadable", { cause: error });
  }
  if (bytes.byteLength === 0) {
    throw new PhotoPipelineError("camera_source_unavailable", "Captured image is empty (0 bytes)");
  }
  return bytes;
}

/** True when the stored value really is raw bytes rather than a Blob reference. */
function rawBytesOf(record: AppraisalPhotoRecord | undefined): Uint8Array | null {
  const value: any = record?.bytes;
  if (!value) return null;
  // Duck-typed on purpose: a value deserialised by IndexedDB can come from a
  // different realm, where `instanceof ArrayBuffer` is false.
  if (ArrayBuffer.isView?.(value)) {
    const view = new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
    return view.byteLength > 0 ? view : null;
  }
  if (typeof value.byteLength === "number" && typeof value.slice === "function" && value.size === undefined) {
    return value.byteLength > 0 ? new Uint8Array(value) : null;
  }
  return null;
}

function withTimeout<T>(promise: Promise<T>, ms: number, onTimeout: () => Error): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(onTimeout()), ms);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (error) => { clearTimeout(timer); reject(error); },
    );
  });
}

/**
 * Materialise a record's durable bytes, bounded by a timeout.
 *
 * v2 records resolve synchronously from the stored ArrayBuffer. v1 (Blob)
 * records fall back to reading the Blob — which is exactly the operation that
 * can stall on iOS, hence the bound. Every failure is categorised as a durable
 * storage problem, NEVER as a camera problem: by this point the camera source
 * is long gone and is not the thing being read.
 */
/** Metadata-only photo record (no `bytes`/`blob`); see `getAllRecordSummaries`. */
export type AppraisalPhotoSummary = Omit<AppraisalPhotoRecord, "bytes" | "blob"> & {
  /** Marker: this object carries no image data — re-read before any byte access. */
  readonly __summary: true;
  hasBytes: boolean;
  hasBlob: boolean;
};

export function isPhotoSummary(r: unknown): r is AppraisalPhotoSummary {
  return !!r && (r as { __summary?: unknown }).__summary === true;
}

export function toPhotoSummary(record: AppraisalPhotoRecord): AppraisalPhotoSummary {
  const { bytes, blob, ...rest } = record;
  return { ...rest, __summary: true, hasBytes: bytes !== undefined, hasBlob: blob !== undefined };
}

/** One readonly transaction; the cursor is advanced synchronously in onsuccess so the transaction stays active. */
function scanSummaries(
  db: IDBDatabase,
  open: (store: IDBObjectStore) => IDBRequest<IDBCursorWithValue | null>,
): Promise<AppraisalPhotoSummary[]> {
  return new Promise((resolve, reject) => {
    const out: AppraisalPhotoSummary[] = [];
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = open(tx.objectStore(STORE_NAME));
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;
      out.push(toPhotoSummary(cursor.value as AppraisalPhotoRecord));
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => resolve(out);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("Photo transaction aborted"));
  });
}

export async function materialisePhotoBytes(
  record: AppraisalPhotoRecord,
  opts: { timeoutMs?: number } = {},
): Promise<Uint8Array> {
  const timeoutMs = opts.timeoutMs ?? DURABLE_READ_TIMEOUT_MS;
  // Defensive: a metadata-only summary never reaches byte code paths by design;
  // if one does, re-read the full record rather than treating it as byte-less.
  if (isPhotoSummary(record)) {
    const full = await appraisalPhotoDB.getRecord(record.appraisalId, record.localId);
    if (!full) throw new PhotoPipelineError("durable_storage_read_failed", "Local photo record could not be re-read");
    record = full;
  }
  const raw = rawBytesOf(record);
  if (raw) return raw;

  if (record?.bytes !== undefined && !raw) {
    throw new PhotoPipelineError("durable_storage_corrupt", "Durable photo bytes are empty (0 bytes)");
  }
  if (!record?.blob) {
    throw new PhotoPipelineError("durable_storage_read_failed", "Local photo data is missing (no durable bytes)");
  }
  if (typeof record.blob.size === "number" && record.blob.size === 0) {
    throw new PhotoPipelineError("durable_storage_corrupt", "Local photo data is unreadable (0 bytes)");
  }

  let bytes: Uint8Array;
  try {
    bytes = await withTimeout(
      blobToBytes(record.blob),
      timeoutMs,
      () => new PhotoPipelineError(
        "durable_storage_read_failed",
        `Durable photo read timed out after ${Math.round(timeoutMs / 1000)}s`,
        { timedOut: true },
      ),
    );
  } catch (error: any) {
    if (error instanceof PhotoPipelineError) throw error;
    throw new PhotoPipelineError(
      "durable_storage_read_failed",
      error?.message || "Durable photo bytes could not be read",
      { cause: error },
    );
  }
  if (bytes.byteLength === 0) {
    throw new PhotoPipelineError("durable_storage_corrupt", "Local photo data is unreadable (0 bytes)");
  }
  return bytes;
}

/**
 * Length of a record's *durable raw bytes* as actually stored (v2), or null
 * when the record carries no usable raw bytes (e.g. a legacy Blob reference).
 * Used to verify a legacy → v2 migration by independent read-back.
 */
export function readableRawByteLength(record: AppraisalPhotoRecord | undefined): number | null {
  const raw = rawBytesOf(record);
  return raw ? raw.byteLength : null;
}

/** A Blob view over a record's durable bytes, for previews and File payloads. */
export function photoRecordBlob(record: AppraisalPhotoRecord | undefined): Blob | null {
  if (!record) return null;
  const raw = rawBytesOf(record);
  if (raw) return new Blob([raw as BlobPart], { type: record.mimeType || "image/jpeg" });
  return record.blob ?? null;
}

export const appraisalPhotoDB = {

  /** Save a photo's raw bytes immediately — returns the record */
  async savePhoto(
    appraisalId: string,
    localId: string,
    category: string,
    blob: Blob,
    /**
     * Destination assigned IN THE SAME WRITE as the bytes (1.76.x race fix):
     * a new capture never exists as `pending_local` without its intended
     * Storage path, so no upload pass can invent a different name for it.
     * `createdAt` must be the timestamp embedded in `targetPath`.
     */
    options: { targetPath?: string; createdAt?: number } = {},
  ): Promise<AppraisalPhotoRecord> {
    // Extract the bytes from the camera result BEFORE anything is persisted or
    // referenced. Nothing downstream may exist unless real bytes were stored.
    const startedAt = Date.now();
    let bytes: Uint8Array;
    try {
      bytes = await readCameraBytes(blob);
    } catch (error) {
      logOfflineEvent("photo_write_failure", {
        appraisalId,
        store: "appraisal_photos",
        subject: `${category}:${localId}`,
        stage: "camera_read",
        errorKind: (error as PhotoPipelineError)?.kind ?? "camera_source_unavailable",
        durationMs: Date.now() - startedAt,
        error,
      });
      // Preserve the historic wording so capture-time messaging is unchanged.
      if ((error as PhotoPipelineError)?.kind === "camera_source_unavailable") {
        const message = (error as Error).message;
        throw new Error(
          /empty/i.test(message)
            ? message
            : "Captured image file no longer exists (camera source unavailable)",
        );
      }
      throw error;
    }

    const mimeType = blob.type || "image/jpeg";
    // H3 — integrity metadata for the EXACT bytes about to be persisted (and
    // therefore the exact bytes that will later be uploaded). Best effort: a
    // runtime without Web Crypto yields null and the capture proceeds exactly
    // as it did before 1.60.51.
    const sha256 = await sha256Hex(bytes);
    const record: AppraisalPhotoRecord = {
      id: `${appraisalId}:${localId}`,
      appraisalId,
      localId,
      category,
      bytes: toArrayBuffer(bytes),
      byteLength: bytes.byteLength,
      expectedByteLength: bytes.byteLength,
      sha256,
      mimeType,
      schemaVersion: PHOTO_SCHEMA_VERSION,
      status: "pending_local",
      storagePath: null,
      ...(options.targetPath ? { targetPath: options.targetPath } : {}),
      createdAt: options.createdAt ?? Date.now(),
      updatedAt: Date.now(),
      persistedAt: Date.now(),
    };

    try {
      await withDb((db) => new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        tx.objectStore(STORE_NAME).put(record);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error || new Error("Photo transaction aborted"));
      }));

      // ── Persistence verification, NOT memory verification ──
      // The write transaction has completed; this is a brand new read
      // transaction returning a freshly deserialised record.
      const persisted = await this.getRecord(appraisalId, localId);
      const persistedBytes = rawBytesOf(persisted);
      if (!persisted || !persistedBytes) {
        throw new PhotoPipelineError("durable_storage_write_failed", `Photo persistence verification failed for ${localId}`);
      }
      if (persistedBytes.byteLength !== bytes.byteLength) {
        throw new PhotoPipelineError("durable_storage_corrupt", `Photo read-back verification failed for ${localId}`);
      }
      // The retrieved bytes must be convertible to a readable image payload.
      const payload = new Blob([persistedBytes as BlobPart], { type: persisted.mimeType || mimeType });
      const payloadBytes = await blobToBytes(payload);
      if (payloadBytes.byteLength !== bytes.byteLength) {
        throw new PhotoPipelineError("durable_storage_corrupt", `Photo payload verification failed for ${localId}`);
      }

      // H3 — read-back integrity. The persisted bytes are hashed from a fresh
      // transaction; a mismatch here is a genuine durable-storage corruption
      // and uses the EXISTING corruption classification (unchanged behaviour:
      // the capture is rejected at the gate, no reference is ever created).
      if (sha256) {
        const persistedHash = await sha256Hex(persistedBytes);
        if (persistedHash && persistedHash !== sha256) {
          throw new PhotoPipelineError("durable_storage_corrupt", `Photo hash read-back verification failed for ${localId}`);
        }
        logOfflineEvent("photo_integrity_hash_created", {
          appraisalId,
          store: "appraisal_photos",
          subject: `${category}:${localId}`,
          localId,
          bytes: bytes.byteLength,
          expectedByteLength: bytes.byteLength,
          sha256,
          verifiedOnReadBack: Boolean(persistedHash),
          schemaVersion: PHOTO_SCHEMA_VERSION,
        });
      }

      logOfflineEvent("photo_write_success", {
        appraisalId,
        store: "appraisal_photos",
        subject: `${category}:${localId}`,
        bytes: bytes.byteLength,
        durationMs: Date.now() - startedAt,
        durable: true,
        schemaVersion: PHOTO_SCHEMA_VERSION,
      });
      logOfflineEvent("durable_persist_success", {
        appraisalId,
        store: "appraisal_photos",
        subject: `${category}:${localId}`,
        bytes: bytes.byteLength,
        representation: "raw_bytes",
        durationMs: Date.now() - startedAt,
      });
      return persisted;
    } catch (error) {
      // Quota exhaustion is a distinct, explicitly classified write failure.
      // Nothing already stored is deleted or archived because of it; the caller
      // (persistCapturedPhotoDurably) keeps its existing fallback behaviour.
      const quota = isQuotaExceededError(error);
      const errorKind = (error as PhotoPipelineError)?.kind
        ?? (quota ? "durable_storage_quota_exceeded" : "durable_storage_write_failed");
      logOfflineEvent("photo_write_failure", {
        appraisalId,
        store: "appraisal_photos",
        subject: `${category}:${localId}`,
        bytes: bytes.byteLength,
        durationMs: Date.now() - startedAt,
        errorKind,
        quotaExceeded: quota,
        schemaVersion: PHOTO_SCHEMA_VERSION,
        error,
      });
      throw error;
    }
  },

  /**
   * Upgrade a legacy v1 (Blob) record in place once its bytes have been read
   * successfully, so the next session never has to resolve a Blob again.
   */
  async upgradeToRawBytes(appraisalId: string, localId: string, bytes: Uint8Array, mimeType?: string): Promise<void> {
    if (!bytes || bytes.byteLength === 0) return;
    await this.patch(appraisalId, localId, {
      bytes: toArrayBuffer(bytes),
      byteLength: bytes.byteLength,
      mimeType: mimeType || "image/jpeg",
      schemaVersion: PHOTO_SCHEMA_VERSION,
    } as Partial<AppraisalPhotoRecord>).catch(() => {});
  },

  /** Update status and optionally set storage path */
  async updateStatus(
    appraisalId: string,
    localId: string,
    status: PhotoStatus,
    storagePath?: string,
  ): Promise<void> {
    const id = `${appraisalId}:${localId}`;
    return withDb((db) => new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const record = getReq.result as AppraisalPhotoRecord | undefined;
        if (record) {
          record.status = status;
          if (storagePath) record.storagePath = storagePath;
          record.updatedAt = Date.now();
          store.put(record);
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error("Photo transaction aborted"));
    }));
  },

  /** Get all photos for an appraisal */
  async getPhotosForAppraisal(appraisalId: string): Promise<AppraisalPhotoRecord[]> {
    return withDb((db) => new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const index = tx.objectStore(STORE_NAME).index("by_appraisal");
      const req = index.getAll(appraisalId);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }));
  },

  /** Get photos by category prefix for an appraisal */
  async getPhotosByCategory(appraisalId: string, categoryPrefix: string): Promise<AppraisalPhotoRecord[]> {
    const all = await this.getPhotosForAppraisal(appraisalId);
    return all.filter(p => p.category.startsWith(categoryPrefix));
  },

  /** Delete a single photo */
  async deletePhoto(appraisalId: string, localId: string): Promise<void> {
    const id = `${appraisalId}:${localId}`;
    return withDb((db) => new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error("Photo transaction aborted"));
    }));
  },

  /** Delete all photos for an appraisal (on completion/clear) */
  async clearAppraisal(appraisalId: string): Promise<void> {
    const photos = await this.getPhotosForAppraisal(appraisalId);
    if (photos.length === 0) return;
    return withDb((db) => new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      for (const p of photos) store.delete(p.id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error("Photo transaction aborted"));
    }));
  },

  /** Get all photos that need uploading (pending_local or failed) */
  async getPendingUploads(appraisalId: string): Promise<AppraisalPhotoRecord[]> {
    const all = await this.getPhotosForAppraisal(appraisalId);
    return all.filter(p => p.status === "pending_local" || p.status === "failed");
  },

  /** Get storagePath for a single photo WITHOUT loading the bytes (memory-safe for native) */
  async getStoragePath(appraisalId: string, localId: string): Promise<string | null> {
    try {
      const id = `${appraisalId}:${localId}`;
      return withDb((db) => new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const req = tx.objectStore(STORE_NAME).get(id);
        req.onsuccess = () => {
          const record = req.result as AppraisalPhotoRecord | undefined;
          resolve(record?.storagePath || null);
        };
        req.onerror = () => resolve(null);
      }));
    } catch {
      return null;
    }
  },

  /** Fetch a single record (loads its bytes) */
  async getRecord(appraisalId: string, localId: string): Promise<AppraisalPhotoRecord | undefined> {
    const id = `${appraisalId}:${localId}`;
    return withDb((db) => new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).get(id);
      req.onsuccess = () => resolve(req.result as AppraisalPhotoRecord | undefined);
      req.onerror = () => resolve(undefined);
    }));
  },

  /** Patch arbitrary durable-queue fields on a record */
  async patch(
    appraisalId: string,
    localId: string,
    patch: Partial<Omit<AppraisalPhotoRecord, "id" | "appraisalId" | "localId">>,
  ): Promise<void> {
    const id = `${appraisalId}:${localId}`;
    let missingRecord = false;
    return withDb((db) => new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const record = getReq.result as AppraisalPhotoRecord | undefined;
        if (record) {
          store.put({ ...record, ...patch, updatedAt: Date.now() });
        } else {
          // The patch contract is unchanged (a missing record is still a no-op
          // that resolves), but the condition is no longer invisible: a state
          // transition addressed to a record that is gone is now observable.
          missingRecord = true;
        }
      };
      tx.oncomplete = () => {
        if (missingRecord) {
          try {
            logOfflineEvent("photo_patch_missing_record", {
              appraisalId,
              store: "appraisal_photos",
              subject: localId,
              patchedFields: Object.keys(patch),
            });
          } catch { /* telemetry must never break the pipeline */ }
        }
        resolve();
      };
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error("Photo transaction aborted"));
    }));
  },

  /** All records across every appraisal (durable queue scan) — loads every photo's bytes. */
  async getAllRecords(): Promise<AppraisalPhotoRecord[]> {
    return withDb((db) => new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).getAll();
      req.onsuccess = () => resolve(req.result as AppraisalPhotoRecord[]);
      req.onerror = () => reject(req.error);
    }), "getAllRecords:appraisal_photos");
  },

  /**
   * iOS 18.7 hotfix — status/metadata scan WITHOUT retaining image bytes.
   * Walks the store one record at a time and keeps everything except `bytes`
   * and `blob`, so a status check never holds the whole photo backlog in
   * memory or returns it in one large result (the whole-store `getAll` was the
   * read WebKit failed on). IndexedDB still deserialises each value as the
   * cursor passes it; that is bounded to one photo at a time. Use full records
   * (`getRecord` / `loadFullRecord`) only for an actual upload or byte check.
   */
  async getAllRecordSummaries(): Promise<AppraisalPhotoSummary[]> {
    return withDb((db) => scanSummaries(db, (store) => store.openCursor()), "getAllRecordSummaries:appraisal_photos");
  },

  /** Per-appraisal variant of `getAllRecordSummaries` (index `by_appraisal`). */
  async getSummariesForAppraisal(appraisalId: string): Promise<AppraisalPhotoSummary[]> {
    return withDb((db) => scanSummaries(db, (store) => store.index("by_appraisal").openCursor(IDBKeyRange.only(appraisalId))),
      "getSummariesForAppraisal:appraisal_photos");
  },

  /** Full record (with bytes) for a summary, or null if it no longer exists / cannot be read. */
  async loadFullRecord(summary: Pick<AppraisalPhotoRecord, "appraisalId" | "localId">): Promise<AppraisalPhotoRecord | null> {
    return (await this.getRecord(summary.appraisalId, summary.localId)) ?? null;
  },

  /** Create an object URL from a stored photo (caller must revoke) */
  createPreviewUrl(record: AppraisalPhotoRecord): string {
    const blob = photoRecordBlob(record);
    if (!blob) throw new Error("Photo has no readable local data");
    return URL.createObjectURL(blob);
  },
};
